/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Test tool. Simulates the native app's AppBridge so the full `/app-bridge`
 * webview flow can be exercised in a plain browser, with no device — on the local
 * dev server AND on deployed non-production builds (staging).
 *
 * Activates only when BOTH hold:
 *   1. the environment is on MOCK_ENABLED_ENVS (or it's the localhost dev server), and
 *   2. the URL carries `?mockBridge=1`.
 * So it is inert on normal page loads even on staging, and cannot run on prod.
 *
 * SAFETY: the env check below is an ALLOWLIST written as an explicit `===` chain,
 * for two reasons. (1) Every comparison is against a Vite-inlined
 * `import.meta.env.*` literal, so in a prod build the whole condition folds to
 * `false` and the minifier drops this entire module — including its dynamic
 * imports — from the bundle; staging keeps it. (2) Never add a production env and never rewrite it as
 * a "not prod" check: a new prod-like environment must fail closed (mock off), not
 * open. Forging the native bridge + minting tokens on prod could drive the buy flow
 * into a REAL Stripe purchase. `import.meta.env.DEV` is only ever true on the local
 * dev server, never a deployed build, so it is always safe to allow.
 *
 * Must be the FIRST import in `index.tsx`: `isIOSWebView` in webviewHelpers is a
 * module-load-time constant, so `window.webkit` has to exist before the app graph
 * evaluates.
 *
 * The access token it returns is a REAL Firebase id_token, minted from the current
 * session (one-time-token -> custom_token -> getIdToken), because CreateSession
 * validates it server-side. Requires an already-logged-in session cookie.
 */
if (
  (import.meta.env.DEV ||
    import.meta.env.VITE_ENVIRONMENT === 'dev' ||
    import.meta.env.VITE_ENVIRONMENT === 'staging' ||
    import.meta.env.VITE_ENVIRONMENT === 'luna-stg' ||
    import.meta.env.VITE_ENVIRONMENT === 'homiq-stg' ||
    import.meta.env.VITE_ENVIRONMENT === 'haloview-stg' ||
    import.meta.env.VITE_ENVIRONMENT === 'waiscen-stg' ||
    import.meta.env.VITE_ENVIRONMENT === 'newaieye-stg') &&
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).has('mockBridge')
) {
  const mintAccessToken = async (): Promise<string> => {
    const { GetOneTimeToken, VerifyOneTimeToken } =
      await import('services/api/UserApi');
    const generated = await GetOneTimeToken();
    console.warn('[mockBridge] one-time-token response:', generated?.data);
    const oneTimeToken =
      (generated as any)?.data?.one_time_token ??
      (generated as any)?.data?.token ??
      (generated as any)?.data;

    const verified = await VerifyOneTimeToken(oneTimeToken);
    console.warn('[mockBridge] verify response:', verified?.data);
    const customToken = (verified as any)?.data?.custom_token;
    if (!customToken) throw new Error('no custom_token in verify response');

    const { getFirebaseAuth } = await import('configs/firebaseConfig');
    const { auth } = await getFirebaseAuth();
    const { signInWithCustomToken } = await import('firebase/auth');
    const credential = await signInWithCustomToken(auth, customToken);
    return credential.user.getIdToken();
  };

  const messageHandlers = new Proxy(
    {},
    {
      get: (_target, method: string) => ({
        postMessage: () => {
          if (method !== 'getAccessToken') return; // buttons etc. -> no-op
          mintAccessToken()
            .then((accessToken) => {
              console.warn(
                '[mockBridge] minted access token, invoking callback'
              );
              (window as any).getAccessTokenCallback?.({ accessToken });
            })
            .catch((err) =>
              console.error('[mockBridge] token mint failed:', err)
            );
        }
      })
    }
  );

  (window as any).webkit = { messageHandlers };
  console.warn('[mockBridge] active — simulating native AppBridge (dev only)');
}

export {};
